import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { Mail, CheckCircle, AlertCircle } from "lucide-react";
import { sendEmailVerification } from "firebase/auth";
import { getFirebaseAuth } from "@/lib/firebase";
import { useToast } from "@/components/ux/Toast";
import tracking from "@/lib/tracking";
import { useT } from "@/lib/i18n";

interface EmailVerificationBannerProps {
  onVerified?: () => void;
}

export default function EmailVerificationBanner({ onVerified }: EmailVerificationBannerProps) {
  const [isSending, setIsSending] = useState(false);
  const [lastSentAt, setLastSentAt] = useState<number | null>(null);
  const toast = useToast();
  const { t } = useT("app");

  const auth = getFirebaseAuth();
  const user = auth.currentUser;

  // Don't show if no user, no email, or already verified
  if (!user || !user.email || user.emailVerified) {
    return null;
  }

  // Cooldown: prevent resending within 60 seconds
  const cooldownSeconds = 60;
  const canResend = !lastSentAt || Date.now() - lastSentAt > cooldownSeconds * 1000;
  const secondsRemaining = lastSentAt
    ? Math.max(0, Math.ceil((cooldownSeconds * 1000 - (Date.now() - lastSentAt)) / 1000))
    : 0;

  const handleResendVerification = async () => {
    if (!canResend || isSending) return;

    setIsSending(true);
    try {
      await sendEmailVerification(user);
      tracking.emailVerificationResent();
      setLastSentAt(Date.now());
      toast.success(t("auth.emailVerification.sendSuccess"));
    } catch (error: any) {
      console.error("Failed to send verification email:", error);
      if (error.code === "auth/too-many-requests") {
        toast.error(t("auth.emailVerification.tooManyRequests"));
      } else {
        toast.error(t("auth.emailVerification.sendFailed"));
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleRefreshStatus = async () => {
    try {
      await user.reload();
      if (user.emailVerified) {
        toast.success(t("auth.emailVerification.verifySuccess"));
        onVerified?.();
        // Force a re-render by updating auth state
        window.location.reload();
      } else {
        toast.error(t("auth.emailVerification.notVerified"));
      }
    } catch (error) {
      console.error("Failed to refresh user:", error);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="mb-6"
      >
        <div className="card relative overflow-hidden border-2 border-amber-400/50 dark:border-amber-500/50">
          {/* Gradient background */}
          <div className="absolute inset-0 bg-gradient-to-br from-amber-100/80 via-orange-100/40 to-yellow-100/60 dark:from-amber-900/40 dark:via-orange-900/20 dark:to-yellow-900/30" />

          <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            {/* Icon and message */}
            <div className="flex items-start gap-3 flex-1">
              <div className="p-2 rounded-full bg-amber-200/60 dark:bg-amber-800/40">
                <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-neutral-800 dark:text-neutral-100">
                  {t("auth.emailVerification.title")}
                </h3>
                <p
                  className="text-sm text-neutral-600 dark:text-neutral-300 mt-0.5"
                  dangerouslySetInnerHTML={{
                    __html: t("auth.emailVerification.message", {
                      email: user.email,
                    }),
                  }}
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <button
                onClick={handleResendVerification}
                disabled={!canResend || isSending}
                className={`
                  flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors
                  ${
                    canResend && !isSending
                      ? "bg-amber-500 hover:bg-amber-600 text-white"
                      : "bg-neutral-200 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400 cursor-not-allowed"
                  }
                `}
              >
                <Mail className={`w-4 h-4 ${isSending ? "animate-pulse" : ""}`} />
                {isSending
                  ? t("auth.emailVerification.sending")
                  : !canResend
                  ? t("auth.emailVerification.resendCountdown", { seconds: secondsRemaining })
                  : t("auth.emailVerification.resendEmail")}
              </button>

              <button
                onClick={handleRefreshStatus}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-white/60 dark:bg-neutral-800/60 hover:bg-white dark:hover:bg-neutral-700 border border-neutral-200/50 dark:border-neutral-600/50 transition-colors"
                title={t("auth.emailVerification.verified")}
              >
                <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                <span className="hidden sm:inline">{t("auth.emailVerification.verified")}</span>
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

